<template>
    <a
        :href="preview ? undefined : url"
        :target="preview ? undefined : '_blank'"
        class="inline-block"
    >
        <div
            class="flex items-center gap-2 rounded-sm border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-700">
            <div class="flex-shrink-0">
                <img :alt="title" :class="pane? 'h-9 w-9': 'h-8 w-8'" :src="fallbackThumbnail" class="dark:invert">
            </div>
            <div class="flex-1 min-w-0">
                <p :class="pane ? 'text-md' : 'text-sm'"
                   class="font-medium text-gray-900 truncate dark:text-gray-100 max-w-[300px]"
                   v-html="title"></p>
                <p :class="pane ? 'text-sm' : 'text-xs'" class="text-gray-500 dark:text-gray-400">
                    {{ mimetype }}
                    <template v-if="size !== ''">
                        · {{ size }}
                    </template>
                    <template v-if="pageCount > 1">
                        · {{ pageCount }} páginas
                    </template>
                </p>
            </div>
        </div>
        <div v-if="caption && !pane" class="mt-4 text-sm text-gray-600 dark:text-gray-50" v-html="caption"></div>
    </a>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from "vue";
import pdfIcon from '@src/assets/vectors/pdf_icon.svg'
import imageIcon from '@src/assets/vectors/image_icon.svg'
import wordIcon from '@src/assets/vectors/word_icon.svg'
import excelIcon from '@src/assets/vectors/excel_icon.svg'
import pptIcon from '@src/assets/vectors/powerpoint_icon.svg'
import fileIcon from '@src/assets/vectors/file_icon.svg'


const props = defineProps(['data', 'preview', 'isGroup', 'pane'])

const url = ref<string>('')
const caption = ref<string>('')
const title = ref<string>('')
const mimetype = ref<string>('')
const size = ref<string>('')
const pageCount = ref<number>(0)
const thumbnail = ref(null)

onMounted(() => {
    let message = props.isGroup ? props.data : (props.data.message ?? props.data)
    if (message) {
        try {
            let parsed = JSON.parse(message)
            url.value = parsed.url
            caption.value = parsed.caption
            title.value = parsed.title
            mimetype.value = getHumanizedType(parsed.mimetype)
            thumbnail.value = parsed.thumbnail
        } catch (e) {
            url.value = message
            extractInfoFromUrl(message)
        }
    }
})

function extractInfoFromUrl(fileUrl: string) {
    try {
        const urlParts = fileUrl.split('/')
        const fileName = urlParts[urlParts.length - 1]

        if (fileName) {
            const cleanFileName = decodeURIComponent(fileName.split('?')[0])
            title.value = cleanFileName
            const extension = cleanFileName.split('.').pop()?.toLowerCase()
            if (extension) {
                mimetype.value = getMimetypeByExtension(extension)
            }
        }
    } catch (error) {
        console.error('Erro ao extrair informações da URL:', error)
    }
}

function getMimetypeByExtension(extension: string): string {
    const extensionMap: Record<string, string> = {
        'pdf': 'application/pdf',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'txt': 'text/plain',
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
    }

    const mimeType = extensionMap[extension] || 'application/octet-stream'
    return getHumanizedType(mimeType)
}

const mimeTypes: Record<string, string> = {
    'image/jpeg': 'Imagem JPEG',
    'image/png': 'Imagem PNG',
    'image/gif': 'Imagem GIF',
    'application/pdf': 'Documento PDF',
    'text/plain': 'Arquivo de texto',
    'application/zip': 'Arquivo ZIP',
    'application/x-rar-compressed': 'Arquivo RAR',
    'application/x-tar': 'Arquivo TAR',
    'application/x-gzip': 'Arquivo GZIP',
    'application/x-bzip2': 'Arquivo BZIP2',
    'application/x-7z-compressed': 'Arquivo 7Z',
    'application/msword': 'Documento do Word (DOC)',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Documento do Word (DOCX)',
    'application/vnd.ms-excel': 'Planilha do Excel (XLS)',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Planilha do Excel (XLSX)',
    'application/vnd.ms-powerpoint': 'Apresentação do PowerPoint (PPT)',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Apresentação do PowerPoint (PPTX)',
    'application/vnd.oasis.opendocument.text': 'Documento do OpenDocument (ODT)',
    'application/vnd.oasis.opendocument.spreadsheet': 'Planilha do OpenDocument (ODS)',
    'application/vnd.oasis.opendocument.presentation': 'Apresentação do OpenDocument (ODP)',
    'application/vnd.oasis.opendocument.graphics': 'Grafico do OpenDocument (ODG)',
    'application/vnd.oasis.opendocument.chart': 'Grafico do OpenDocument (ODC)',
    'application/vnd.oasis.opendocument.formula': 'Formula do OpenDocument (ODF)',
    'application/vnd.oasis.opendocument.image': 'Imagem do OpenDocument (ODI)',
    'application/vnd.oasis.opendocument.text-master': 'Master do OpenDocument (ODM)',
    'application/vnd.oasis.opendocument.text-web': 'Web do OpenDocument (OWG)',
    'application/vnd.oasis.opendocument.text-template': 'Template do OpenDocument (OTG)',
};

function getHumanizedType(mimeType: string) {
    return mimeTypes[mimeType] || 'Tipo de arquivo desconhecido';
}

const fallbackThumbnail = computed(() => {
    if (mimetype.value.startsWith('Imagem')) {
        return imageIcon
    } else if (mimetype.value.includes('PDF')) {
        return pdfIcon
    } else if (mimetype.value.includes('Word')) {
        return wordIcon
    } else if (mimetype.value.includes('Excel')) {
        return excelIcon
    } else if (mimetype.value.includes('PowerPoint')) {
        return pptIcon
    } else {
        return fileIcon
    }
})

</script>
